import { createMarbleRewards } from './182-marble-rewards.mjs';
import { createMarbleScenes } from './181-marble-scenes.mjs';
import { marbleHint } from '../../../packages/domain/src/62-progressive-idle.mjs';
import { paintIconAction } from './82-icon-actions.mjs';
import { createSampleAudio } from './187-sample-audio.mjs';

/** Private marble tools and finite earned feedback. The card owns immutable
 * state/history; this owner retains only the current level's restart snapshot.
 * Hints consume public projections and never perform a move for the player. */
export function createMarblePresentation(
  ctx,
  {
    host,
    board,
    playfield,
    controls,
    arcade,
    roundLabel,
    runBest,
    feedback,
    undo,
    action,
    readState,
    move,
    allowed,
    restore,
  },
) {
  let initial = readState(),
    generation = 0,
    timer = 0,
    disposed = false,
    sound = false,
    soundWanted = false,
    soundRequest = 0;
  const audio = createSampleAudio({
    initialVolume: 0.32,
    samples: Object.fromEntries(
      ['pickup', 'bounce', 'checkpoint', 'finish'].map((name) => [
        name,
        new URL(`../assets/bob-audio-${name}.wav`, import.meta.url),
      ]),
    ),
  });
  const speaker = action('', 'Turn marble sounds on', toggleSound, 'volume-x');
  speaker.setAttribute('aria-pressed', 'false');
  controls.insertBefore(speaker, undo);
  function paintSound() {
    speaker.setAttribute('aria-pressed', String(sound));
    paintIconAction(
      ctx,
      speaker,
      sound ? 'volume-2' : 'volume-x',
      sound ? 'Turn marble sounds off' : 'Turn marble sounds on',
    );
  }
  async function toggleSound() {
    if (disposed || !allowed()) return;
    const request = ++soundRequest;
    const wanted = (soundWanted = !soundWanted);
    speaker.setAttribute('aria-busy', String(wanted));
    if (wanted) feedback.textContent = (ctx.uiText ?? ((value) => value))('Starting sound…');
    const enabled = await audio.enable(wanted);
    if (disposed || request !== soundRequest) return;
    sound = soundWanted = enabled;
    speaker.setAttribute('aria-busy', 'false');
    paintSound();
    feedback.textContent = (ctx.uiText ?? ((value) => value))(
      sound ? 'Sound on' : wanted ? 'Sound unavailable · tap to retry' : 'Sound off',
    );
    if (sound) audio.play('pickup');
  }
  // Recovery is attempted only from a fresh interaction, never on visibility.
  const resumeSound = async () => {
    if (!sound || !allowed() || document.hidden) return;
    const request = soundRequest;
    const ready = await audio.resume();
    if (disposed || request !== soundRequest || !sound || ready) return;
    sound = soundWanted = false;
    paintSound();
    feedback.textContent = (ctx.uiText ?? ((value) => value))('Sound unavailable · tap to retry');
  };
  board.addEventListener('pointerup', resumeSound);
  board.addEventListener('keydown', resumeSound);
  const scenes = createMarbleScenes(ctx, { host, board, playfield, allowed });
  const animations = new Set();
  const rewards = createMarbleRewards(ctx, playfield);
  const toast = ctx.e('div', 'marble-toast');
  toast.setAttribute('aria-hidden', 'true');
  toast.hidden = true;
  const badge = ctx.e('strong'),
    caption = ctx.e('span');
  toast.append(badge, caption);
  playfield.append(toast);
  const guide = ctx.e('p', 'marble-guide', 'Tap a tube, then a glowing destination.');
  host.insertBefore(guide, controls);
  const restart = action(
    '',
    'Restart this marble puzzle',
    () => {
      if (!allowed()) return;
      restore(initial);
      notify('Fresh start', 'Same puzzle. Take your time.', false, '↶');
    },
    'rotate-ccw',
  );
  const hint = action(
    '',
    'Hint for this marble puzzle',
    () => {
      if (!allowed()) return;
      if (readState().data.selected >= 0) move({ type: 'cell', index: readState().data.selected });
      clearHints();
      const result = marbleHint(board.gameView);
      if (result.kind === 'move') {
        notify(
          `Tube ${result.from + 1} → ${result.to + 1}`,
          'A step on a verified solution.',
          false,
          `${String(result.from + 1).padStart(2, '0')} → ${String(result.to + 1).padStart(2, '0')}`,
        );
        for (const [index, value] of [
          [result.from, 'from'],
          [result.to, 'to'],
        ]) {
          const node = board.querySelector(`[data-cell="${index}"]`);
          if (node) node.dataset.marbleHint = value;
        }
      } else {
        const messages = {
          concealed: ['A little mystery', 'Uncover grey marbles to enable a solution hint.'],
          complete: ['Beautifully sorted!', 'Your next puzzle is ready.'],
          stalled: ['Try a rewind', 'No solution from here. Undo or restart this puzzle.'],
          limit: ['Keep exploring', 'No hint found within the search limit. Try Undo.'],
        };
        notify(...messages[result.kind]);
      }
    },
    'sparkles',
  );
  controls.insertBefore(restart, undo.nextSibling);
  controls.insertBefore(hint, restart.nextSibling);
  paintIconAction(ctx, undo, 'undo-2', 'Undo the last action');
  arcade.hidden = false;
  function clearHints() {
    for (const cell of board.children) delete cell.dataset.marbleHint;
  }
  function cancel(stopSound = true) {
    generation++;
    scenes.cancel();
    if (stopSound) audio.stop();
    rewards.cancel();
    clearTimeout(timer);
    for (const animation of animations) animation.cancel();
    animations.clear();
    toast.hidden = true;
    clearHints();
  }
  function animate(node, frames, options) {
    if (typeof node.animate !== 'function') return null;
    const animation = node.animate(frames, options);
    animations.add(animation);
    void animation.finished.then(
      () => animations.delete(animation),
      () => animations.delete(animation),
    );
    return animation;
  }
  function notify(title, detail, celebrate = false, visual = title) {
    cancel(false);
    if (disposed || document.hidden || !host.isConnected) return;
    badge.textContent = visual;
    caption.textContent = detail;
    feedback.textContent = `${title}. ${detail}`;
    toast.dataset.celebrate = String(celebrate);
    toast.dataset.explanation = String(visual === title);
    toast.hidden = false;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!reduced) {
      animate(
        toast,
        [
          { opacity: 0, transform: 'translateY(8px) scale(.94)' },
          { opacity: 1, transform: 'translateY(-2px) scale(1.02)', offset: 0.65 },
          { opacity: 1, transform: 'translateY(0) scale(1)' },
        ],
        { duration: 300, easing: 'cubic-bezier(.2,.8,.2,1)' },
      );
    }
    if (celebrate) rewards.play({ finale: readState().data.stageComplete });
    timer = setTimeout(
      () => {
        toast.hidden = true;
      },
      celebrate ? 1800 : 2200,
    );
  }
  function update(view) {
    const state = readState();
    speaker.disabled = !allowed();
    if (!allowed()) {
      soundRequest++;
      sound = soundWanted = false;
      void audio.enable(false);
      paintSound();
      hint.disabled = restart.disabled = true;
      cancel();
      return;
    }
    scenes.prepare();
    rewards.prepare();
    if (state.seed !== initial.seed || state.data.stage !== initial.data.stage || state.moves === 0)
      initial = state;
    board.gameView = view;
    roundLabel.setAttribute('aria-label', `Level ${view.stage}`);
    runBest.setAttribute('aria-label', `${state.data.transfers} moves`);
    if (ctx.icon) {
      roundLabel.replaceChildren(
        ctx.icon('gamepad-2'),
        ctx.e('b', '', String(view.stage).padStart(2, '0')),
      );
      runBest.replaceChildren(ctx.icon('hand'), ctx.e('b', '', String(state.data.transfers)));
      const progress = host.querySelector('.game-progress');
      if (progress) {
        progress.setAttribute('aria-label', view.progress.label);
        progress.replaceChildren(
          ...Array.from({ length: view.progress.total }, (_, i) => {
            const pip = ctx.e('i', 'marble-progress-pip', i < view.progress.current ? '✓' : '');
            pip.setAttribute('aria-hidden', 'true');
            pip.dataset.sorted = String(i < view.progress.current);
            return pip;
          }),
        );
      }
    } else {
      roundLabel.textContent = `LEVEL ${String(view.stage).padStart(2, '0')}`;
      runBest.textContent = `${state.data.transfers} ${state.data.transfers === 1 ? 'MOVE' : 'MOVES'}`;
    }
    hint.disabled = state.data.stageComplete || view.moveLimitReached || !allowed();
    restart.disabled = !allowed() || (!state.data.transfers && state.data.selected < 0);
    guide.textContent = state.data.stageComplete
      ? 'All colours home. Beautifully done.'
      : state.data.selected >= 0
        ? `Tube ${state.data.selected + 1} selected · tap a glowing tube to pour.`
        : state.data.transfers
          ? 'Keep matching. Undo is always here.'
          : 'Tap a tube, then a glowing destination.';
  }
  function accepted(previous, next, flight) {
    cancel();
    if (previous.data.stage !== next.data.stage) {
      scenes.chapter(previous, next);
      return;
    }
    if (next.data.transfers <= previous.data.transfers) {
      if (next.data.selected >= 0 && next.data.selected !== previous.data.selected)
        audio.play('pickup', { level: 0.55 });
      return;
    }
    audio.play('bounce', {
      rate:
        1 +
        Math.min(4, next.data.events.find((event) => event.type === 'transfer')?.count || 1) * 0.06,
      level: 0.75,
    });
    const token = ++generation;
    const fromView = board.gameView;
    const event = next.data.events.find((event) => event.type === 'transfer');
    const target = fromView.cells[event.to];
    const complete =
      target.layers.length === 4 &&
      target.layers.every((c) => c !== null && c === target.layers[0]);
    const revealed =
      previous.data.revealed.flat().filter(Boolean).length <
      next.data.revealed.flat().filter(Boolean).length;
    void Promise.resolve(flight?.finished).then(() => {
      if (
        disposed ||
        !allowed() ||
        generation !== token ||
        readState() !== next ||
        document.hidden ||
        !host.isConnected
      )
        return;
      if (next.data.stageComplete) {
        audio.play('finish');
        notify(
          'MARBLE MASTER!',
          `Level ${next.data.stage} · ${next.data.transfers} moves`,
          true,
          '✦ ✦ ✦',
        );
      } else if (complete) {
        audio.play('checkpoint', { level: 0.8 });
        notify(
          'COLOUR COMPLETE!',
          `${fromView.progress.current} of ${fromView.progress.total} colours home`,
          true,
          `✓ ${fromView.progress.current}/${fromView.progress.total}`,
        );
      } else if (revealed)
        notify('Mystery revealed', 'A new colour joins the puzzle.', false, '? → ●');
      else if (event.count > 1)
        notify(
          `${event.count} IN A FLOW!`,
          'Matching marbles travel together.',
          false,
          `● × ${event.count}`,
        );
    });
  }
  const media = window.matchMedia('(prefers-reduced-motion: reduce)');
  const suspend = () => {
    if (document.hidden) {
      soundRequest++;
      if (!sound) {
        soundWanted = false;
        speaker.setAttribute('aria-busy', 'false');
        void audio.enable(false);
      }
      audio.suspend();
    }
    if (document.hidden || media.matches) cancel();
  };
  document.addEventListener('visibilitychange', suspend);
  media.addEventListener?.('change', suspend);
  return {
    update,
    accepted,
    cancel,
    dispose() {
      disposed = true;
      cancel();
      scenes.dispose();
      rewards.dispose();
      soundRequest++;
      audio.dispose();
      speaker.remove();
      board.removeEventListener('pointerup', resumeSound);
      board.removeEventListener('keydown', resumeSound);
      document.removeEventListener('visibilitychange', suspend);
      media.removeEventListener?.('change', suspend);
      restart.remove();
      hint.remove();
      guide.remove();
      toast.remove();
    },
  };
}
